import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDisclosure, useToast } from '@mybridge/ui/hooks';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { createContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addToProfile,
  askTestimonal,
  deleteTestimonial,
  giveTestimonal,
  giveTestimonalForAsked,
  listOfAskedTestimonialRequest,
  listOfGiveTestimonial,
  listOfGiveTestimonialPendingRequest,
  listOfReceivedTestimonial,
  listOfReceivedTestimonialPending,
  remindForTestimonial,
} from 'v4/store/actions/testimonial.actions';
import { ANALYTICS_EVENTS, trackEvent } from 'v4/utils/analyticsEvents';
import { flattenQueryResults, getNextPageFromURL } from 'v4/utils/utils';

export const MyTestimonialsContext = createContext();

export const useMyTestiomonialsContext = ({
  givenTestimonialsQueryEnable = false,
  givenTestimonialsPendingRequestsQueryEnable = false,
  receivedTestimonialsQueryEnable = false,
  receivedTestimonialsPendingRequestsQueryEnable = false,
  requestedTestimonialsQueryEnable = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslator();
  const { userProfileInfo } = useSelector((s) => s.userProfile);
  const toast = useToast({ position: 'top', isClosable: true });
  const giveTestimonialDisc = useDisclosure();
  const askTestimonialDisc = useDisclosure();
  const testimonialReminderDisc = useDisclosure();
  const withdrawTestimonialDisc = useDisclosure();
  const [tab, setTab] = useState(0);
  const [selected, setSelected] = useState();
  const [reminded, setReminded] = useState([]);

  const givenTestimonialsQuery = useInfiniteQuery({
    queryKey: ['given-testimonials'],
    queryFn: async ({ queryKey, pageParam }) => {
      try {
        const resp = await dispatch(listOfGiveTestimonial({ pageParam }));
        return resp?.payload ?? {};
      } catch (e) {
        console.error(e);
      }
    },
    getNextPageParam: (lastPage) => getNextPageFromURL(lastPage?.next),
    refetchOnWindowFocus: false,
    enabled: givenTestimonialsQueryEnable,
  });

  const givenTestimonialsPendingRequestsQuery = useInfiniteQuery({
    queryKey: ['given-testimonials-pending-requests'],
    queryFn: async ({ queryKey, pageParam }) => {
      try {
        const resp = await dispatch(
          listOfGiveTestimonialPendingRequest({ pageParam })
        );
        return resp?.payload ?? {};
      } catch (e) {
        console.error(e);
      }
    },
    getNextPageParam: (lastPage) => getNextPageFromURL(lastPage?.next),
    refetchOnWindowFocus: false,
    enabled: givenTestimonialsPendingRequestsQueryEnable,
  });

  const receivedTestimonialsQuery = useInfiniteQuery({
    queryKey: ['received-testimonials'],
    queryFn: async ({ queryKey, pageParam }) => {
      try {
        const resp = await dispatch(listOfReceivedTestimonial({ pageParam }));
        return resp?.payload ?? {};
      } catch (e) {
        console.error(e);
      }
    },
    getNextPageParam: (lastPage) => getNextPageFromURL(lastPage?.next),
    refetchOnWindowFocus: false,
    enabled: receivedTestimonialsQueryEnable,
  });

  const receivedTestimonialsPendingRequestsQuery = useInfiniteQuery({
    queryKey: ['received-testimonials-pending-requests'],
    queryFn: async ({ queryKey, pageParam }) => {
      try {
        const resp = await dispatch(
          listOfReceivedTestimonialPending({ pageParam })
        );
        return resp?.payload ?? {};
      } catch (e) {
        console.error(e);
      }
    },
    getNextPageParam: (lastPage) => getNextPageFromURL(lastPage?.next),
    refetchOnWindowFocus: false,
    enabled: receivedTestimonialsPendingRequestsQueryEnable,
  });

  const requestedTestimonialsQuery = useInfiniteQuery({
    queryKey: ['requested-testimonials'],
    queryFn: async ({ queryKey, pageParam }) => {
      try {
        const resp = await dispatch(
          listOfAskedTestimonialRequest({ pageParam })
        );
        return resp?.payload ?? {};
      } catch (e) {
        console.error(e);
      }
    },
    getNextPageParam: (lastPage) => getNextPageFromURL(lastPage?.next),
    refetchOnWindowFocus: false,
    enabled: requestedTestimonialsQueryEnable,
  });

  /**
   * add testimonial to profile
   */
  const addToProfileMutation = useMutation({
    mutationKey: ['add-to-profile'],
    mutationFn: async (payload) => {
      return (await dispatch(addToProfile(payload)))?.payload;
    },
  });

  /**
   * add testimonial to profile
   */
  const removeFromProfileMutation = useMutation({
    mutationKey: ['remove-from-profile'],
    mutationFn: async (payload) => {
      return (await dispatch(addToProfile(payload)))?.payload;
    },
  });

  const addToProfile_ = async (testimonial) => {
    try {
      const resp = await addToProfileMutation?.mutateAsync({
        id: testimonial?.id,
        status: 'Accept',
        is_added_to_profile: true,
      });
      if (resp?.state_code >= 400) {
        toast({
          title: t('toast.addTestimonialError') || 'Unable to add testimonial to your profile!',
          status: 'error',
        });
      } else {
        toast({
          title: t('toast.addToProfileSuccess') || 'Added to profile successfully!',
          status: 'success',
        });
        receivedTestimonialsQuery?.refetch?.();
        receivedTestimonialsPendingRequestsQuery?.refetch?.();
      }
    } catch (e) {
      toast({
        title: t('toast.addTestimonialErrorGeneric') || 'Error while adding testimonial to profile!',
        status: 'error',
      });
    }
  };

  const removeFromProfile_ = async (testimonial) => {
    try {
      const resp = await addToProfileMutation?.mutateAsync({
        id: testimonial?.id,
        status: 'Accept',
        is_added_to_profile: false,
      });
      if (resp?.state_code >= 400) {
        toast({
          title: t('toast.hideTestimonialError') || 'Unable to hide testimonial!',
          status: 'error',
        });
      } else {
        toast({
          title: t('toast.testimonialHiddenSuccess') || 'Testimonial hidden successfully!',
          status: 'success',
        });
        receivedTestimonialsQuery?.refetch?.();
        receivedTestimonialsPendingRequestsQuery?.refetch?.();
      }
    } catch (e) {
      toast({
        title: t('toast.hideTestimonialErrorGeneric') || 'Error while hiding testimonial!',
        status: 'error',
      });
    }
  };

  /**
   * delete testimonial
   */
  const deleteTestimonialMutation = useMutation({
    mutationKey: ['delete-testimonial'],
    mutationFn: async (payload) => {
      return (await dispatch(deleteTestimonial(payload)))?.payload;
    },
  });

  const deleteTestimonial_ = async (testimonial) => {
    try {
      const resp = await deleteTestimonialMutation?.mutateAsync(
        testimonial?.id
      );
      if (resp?.state_code >= 400) {
        toast({
          title: t('toast.removeTestimonialError') || 'Unable to remove testimonial !',
          status: 'error',
        });
      } else {
        toast({
          title: t('toast.testimonialRemovedSuccess') || 'Testimonial removed successfully!',
          status: 'success',
        });
        if (tab === 0) {
          receivedTestimonialsQuery?.refetch?.();
          receivedTestimonialsPendingRequestsQuery?.refetch?.();
        }
        if (tab === 1) {
          givenTestimonialsQuery?.refetch?.();
          givenTestimonialsPendingRequestsQuery?.refetch?.();
        }
        if (tab === 2) {
          requestedTestimonialsQuery?.refetch?.();
        }
      }
    } catch (e) {
      toast({
        title: t('toast.removeTestimonialErrorGeneric') || 'Error while removing testimonial!',
        status: 'error',
      });
    }
  };

  /**
   * Remind testimonial
   */
  const remindTestimonialMutation = useMutation({
    mutationKey: ['remind-testimonial'],
    mutationFn: async (payload) => {
      return (await dispatch(remindForTestimonial(payload)))?.payload;
    },
  });

  const remindTestimonial_ = async (testimonial) => {
    try {
      const resp = await remindTestimonialMutation?.mutateAsync(testimonial);
      if (resp?.state_code >= 400) {
        toast({
          title: t('toast.sendReminderError') || 'Unable to send a reminder !',
          status: 'error',
        });
      } else {
        addToReminded(testimonial?.id);
        toast({
          title: resp?.message || t('toast.reminderSentSuccess') || 'Sent reminder for testimonial successfuly!',
          status: 'success',
        });
      }
    } catch (e) {
      console.log(e);
      toast({
        title: t('toast.sendReminderErrorGeneric') || 'Error while sending reminder for testimonial!',
        status: 'error',
      });
    }
  };

  /**
   * give testimonial
   */
  const giveTestimonialMutation = useMutation({
    mutationKey: ['give-testimonial'],
    mutationFn: async (payload) => {
      const fn = payload?.id ? giveTestimonalForAsked : giveTestimonal;
      return (await dispatch(fn(payload)))?.payload;
    },
  });

  const giveTestimonial_ = async (testimonial) => {
    try {
      const resp = await giveTestimonialMutation?.mutateAsync(testimonial);
      if (resp?.state_code >= 400) {
        toast({
          duration: 3000,
          title: t('toast.giveTestimonialError') || 'Unable to give testimonial !',
          status: 'error',
        });
      } else {
        trackEvent(ANALYTICS_EVENTS.GiveTestimonial, userProfileInfo?.slug || 'anonymous');
        toast({
          duration: 3000,
          title: selected?.asker?.id ? "Testimonal update successfully" : t('toast.testimonialGivenSuccess') || 'Testimonial given successfully!',
          status: 'success',
        });
        givenTestimonialsQuery?.refetch?.();
        givenTestimonialsPendingRequestsQuery?.refetch?.();
        giveTestimonialDisc?.onClose?.();
      }
    } catch (e) {
      toast({
        duration: 3000,
        title: t('toast.giveTestimonialErrorGeneric') || 'Error while giving testimonial!',
        status: 'error',
      });
    }
  };

  /**
   * ask testimonial
   */
  const askTestimonialMutation = useMutation({
    mutationKey: ['ask-testimonial'],
    mutationFn: async (payload) => {
      return (await dispatch(askTestimonal(payload)))?.payload;
    },
  });

  const askTestimonial_ = async (testimonial) => {
    try {
      const resp = await askTestimonialMutation?.mutateAsync(testimonial);
      if (!resp?.message || !resp?.message?.includes('Sent successfully')) {
        if (resp?.message?.startsWith('Duplicate')) {
          toast({
            title: t('toast.requestAlreadySent') || 'Request already sent!',
            status: 'error',
          });
        } else {
          toast({
            title: resp?.message,
            status: 'error',
          });
        }
      } else {
        trackEvent(ANALYTICS_EVENTS.AskTestimonial, userProfileInfo?.slug || 'anonymous');
        toast({
          title: t('toast.testimonialRequestSuccess') || 'Testimonial request sent successfully!',
          status: 'success',
        });
        requestedTestimonialsQuery?.refetch?.();
        askTestimonialDisc?.onClose?.();
      }
    } catch (e) {
      toast({
        title: t('toast.askTestimonialError') || 'Error while asking for a testimonial!',
        status: 'error',
      });
    }
  };

  const addToReminded = (id) => {
    if (!reminded?.includes?.(id)) {
      reminded?.push(id);
    }
  };
  const removeFromReminded = (id) => {
    const r_ = reminded?.filter((v) => v !== id);
    setReminded(r_);
  };

  const givenTestimonials = flattenQueryResults(givenTestimonialsQuery.data)||[];
  // const givenTestimonials = givenTestimonialsQuery.data?.pages?.[0];
  // const givenTestimonialsPendingRequests =
  //   givenTestimonialsPendingRequestsQuery.data?.pages?.[0];
  const givenTestimonialsPendingRequests = flattenQueryResults(
    givenTestimonialsPendingRequestsQuery.data
  );

  // const receivedTestimonials = receivedTestimonialsQuery.data?.pages?.[0];
  const receivedTestimonials = flattenQueryResults(
    receivedTestimonialsQuery.data
  );
  // const receivedTestimonialsPendingRequests =
  //   receivedTestimonialsPendingRequestsQuery.data?.pages?.[0];
  const receivedTestimonialsPendingRequests = flattenQueryResults(
    receivedTestimonialsPendingRequestsQuery.data
  );

  // const requestedTestimonials = requestedTestimonialsQuery.data?.pages?.[0];
  const requestedTestimonials = flattenQueryResults(
    requestedTestimonialsQuery.data
  );

  return {
    givenTestimonialsQuery,
    givenTestimonials,
    givenTestimonialsPendingRequestsQuery,
    givenTestimonialsPendingRequests,
    receivedTestimonialsQuery,
    receivedTestimonials,
    receivedTestimonialsPendingRequestsQuery,
    receivedTestimonialsPendingRequests,
    requestedTestimonialsQuery,
    requestedTestimonials,
    userProfileInfo,
    giveTestimonialDisc,
    askTestimonialDisc,
    testimonialReminderDisc,
    withdrawTestimonialDisc,

    // add to profile
    addToProfileMutation,
    addToProfile: addToProfile_,

    //hide 
    removeFromProfileMutation,
    removeFromProfile:removeFromProfile_,

    // withdraw / remove testimonial
    deleteTestimonialMutation,
    deleteTestimonial: deleteTestimonial_,

    //give testimonial
    giveTestimonialMutation,
    giveTestimonial: giveTestimonial_,

    //ask testimonial
    askTestimonialMutation,
    askTestimonial: askTestimonial_,

    //tab
    tab,
    setTab,

    // selected testimonial for operation
    selected,
    setSelected,

    // remind
    remindTestimonialMutation,
    remindTestimonial: remindTestimonial_,
    reminded,
  };
};
